import { useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

type UrlFilterPrimitive = string | number | boolean;

// Widen the literal types inferred from a `defaults` object (e.g. `false`, `''`, `15`) back to
// their base primitives, so consumers get `boolean`/`string`/`number` rather than literal types.
type WidenPrimitive<V> = V extends boolean ? boolean : V extends number ? number : V extends string ? string : V;
type WidenFilters<T> = { [K in keyof T]: WidenPrimitive<T[K]> };

export type UrlFilters<T extends Record<string, UrlFilterPrimitive>> = {
    initial: WidenFilters<T>;
    write: (values: WidenFilters<T>) => void;
};

/**
 * Persists a lookup page's filters (search, pagination, sort, toggles) in the URL
 * query string through React Router (`setSearchParams` with `replace`), so navigating
 * away and back (browser back / `navigate(-1)`) restores the same view and the URL is
 * shareable. Going through the router — rather than raw `history.replaceState` — keeps
 * React Router's history state intact, which is what makes back-navigation restore params.
 *
 * `defaults` defines the managed keys and their types: `number` values are parsed
 * with `Number`, `boolean` with `=== 'true'`, everything else stays a string. A
 * value equal to its default is omitted to keep URLs clean. Reads are
 * case-insensitive so external deep-links (e.g. `?searchTerm=`) still resolve; the
 * initial values are read once on mount.
 *
 * Keys are only ever read/written, never cached in memory, so a partner/mode switch
 * that changes the route naturally drops them.
 */
export const useUrlFilters = <T extends Record<string, UrlFilterPrimitive>>(defaults: T): UrlFilters<T> => {

    const [searchParams, setSearchParams] = useSearchParams();

    const keysRef = useRef(Object.keys(defaults) as (keyof T & string)[]);

    const initialRef = useRef<T | null>(null);
    if (initialRef.current === null) {

        // Case-insensitive lookup so externally supplied params (any casing) resolve.
        const lowerToValue = new Map<string, string>();
        searchParams.forEach((value, key) => lowerToValue.set(key.toLowerCase(), value));

        const parsed = { ...defaults };
        keysRef.current.forEach(key => {

            const raw = lowerToValue.get(key.toLowerCase());
            if (raw === undefined) return;

            const fallback = defaults[key];
            if (typeof fallback === 'number') {

                const numeric = Number(raw);
                if (!Number.isNaN(numeric)) parsed[key] = numeric as T[typeof key];

            } else if (typeof fallback === 'boolean') {

                parsed[key] = (raw === 'true') as T[typeof key];

            } else {

                parsed[key] = raw as T[typeof key];

            }

        });
        initialRef.current = parsed;

    }

    const write = (values: WidenFilters<T>) => {

        // Read the live query so params owned by other code (PType, create, …) are preserved.
        const params = new URLSearchParams(window.location.search);

        // Drop any existing case-variant of a managed key so a write never leaves duplicates.
        const managedLower = new Set(keysRef.current.map(key => key.toLowerCase()));
        [...params.keys()].forEach(key => {

            if (managedLower.has(key.toLowerCase())) params.delete(key);

        });

        keysRef.current.forEach(key => {

            const value = values[key];
            const isDefault = value === defaults[key] || value === '' || value === null || value === undefined;
            if (!isDefault) params.set(key, String(value));

        });

        // replace: true keeps a single history entry per view (no spam) while still letting
        // browser back and navigate(-1) return to this URL with its params.
        setSearchParams(params, { replace: true });

    };

    return { initial: initialRef.current as WidenFilters<T>, write };

};
